import { useState, useEffect } from 'react'
import styles from './SourceDetailsModal.module.css'
import AssociatedProductInput from './AssociatedProductInput'
import ProductChip from './ProductChip'
import ContentSetList from './ContentSetList'
import Icon from './Icon'
import { sourceTypePalette, DocumentGlyph, WebGlyph } from './sourceVisuals'
import { formatDate } from '../utils/formatDate'

function formatUser(userId) {
  return userId ? `User #${userId}` : '—'
}

// Web sources store a bare host/path, with no scheme — add one.
function ensureHref(url) {
  return /^https?:\/\//i.test(url ?? '') ? url : `https://${url}`
}

function EditIcon() {
  return <Icon name="edit" />
}

function CloseIcon() {
  return <Icon name="close" />
}

/**
 * SourceDetailsModal — read-only view of a Knowledge Base source, with a
 * pencil icon to switch to an edit form for the same fields.
 *
 * Only document-level fields are editable here. A content-set's product version
 * is part of its identity, so changing coverage means adding a version or
 * deleting one — both offered against the content-set list.
 *
 * @param {object} props
 * @param {object|null} props.source — modal is hidden when null
 * @param {string[]} [props.products] — the tenant's product catalog, for the picker
 * @param {function(): void} props.onClose
 * @param {function(number, object): Promise<void>} props.onSave
 * @param {function(object): void} [props.onAddVersion]
 * @param {function(object, object): void} [props.onDeleteVersion]
 */
export default function SourceDetailsModal({ source, products = [], onClose, onSave, onAddVersion, onDeleteVersion }) {
  const [editing, setEditing] = useState(false)
  const [description, setDescription] = useState('')
  const [selectedProducts, setSelectedProducts] = useState(/** @type {string[]} */ ([]))
  const [reviewDate, setReviewDate] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(/** @type {string|null} */ (null))

  // Keyed on the id, not the object: the page re-derives `source` from every
  // re-list, and reseeding on that would wipe whatever the user was typing (or
  // drop them out of edit mode) each time a version's status changed.
  useEffect(() => {
    if (!source) return
    seedFrom(source)
    setEditing(false)
    setError(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source?.id])

  if (!source) return null

  function seedFrom(src) {
    setDescription(src.description ?? '')
    setSelectedProducts(src.products ?? [])
    setReviewDate(src.reviewDate ?? '')
  }

  function startEdit() {
    seedFrom(source)
    setError(null)
    setEditing(true)
  }

  function cancelEdit() {
    seedFrom(source)
    setError(null)
    setEditing(false)
  }

  async function handleSubmit(e) {
    e.preventDefault()

    setSaving(true)
    setError(null)
    try {
      await onSave(source.id, {
        description: description.trim(),
        products: selectedProducts,
        reviewDate,
      })
      onClose()
    } catch (err) {
      setError(err.message || 'Failed to save changes.')
    } finally {
      setSaving(false)
    }
  }

  const palette = sourceTypePalette(source.type)
  const isWeb = source.type === 'web'
  const contentSets = source.contentSets ?? []
  const sourceProducts = source.products ?? []

  return (
    <div className={styles.backdrop}>
      <div className={styles.modal}>

        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <span
              className={styles.typeIcon}
              style={{ color: palette.text, borderColor: palette.border, backgroundColor: palette.bg }}
            >
              {isWeb ? <WebGlyph /> : <DocumentGlyph />}
            </span>
            <div className={styles.headerText}>
              <h2 className={styles.title}>{source.name}</h2>
              <span className={styles.subtitle}>{isWeb ? 'Web Source' : 'Document'}</span>
            </div>
          </div>
          <div className={styles.headerActions}>
            {!editing && (
              <button type="button" className={styles.iconBtn} onClick={startEdit} aria-label="Edit source">
                <EditIcon />
              </button>
            )}
            <button type="button" className={styles.iconBtn} onClick={onClose} aria-label="Close modal">
              <CloseIcon />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className={styles.body}>

            {/* Document-level facts — always visible, read-only. Status and page
                counts are per content-set and live in the list below. */}
            <div className={styles.metaGrid}>
              <div className={styles.metaItem}>
                <span className={styles.metaLabel}>Updated</span>
                <span className={styles.metaValue}>{formatDate(source.updatedAt)}</span>
              </div>
              <div className={styles.metaItem}>
                <span className={styles.metaLabel}>Review Date</span>
                <span className={styles.metaValue}>
                  {formatDate(editing ? reviewDate : source.reviewDate)}
                </span>
              </div>
              <div className={styles.metaItem}>
                <span className={styles.metaLabel}>Created By</span>
                <span className={styles.metaValue}>{formatUser(source.createdBy)}</span>
              </div>
              <div className={styles.metaItem}>
                <span className={styles.metaLabel}>Updated By</span>
                <span className={styles.metaValue}>{formatUser(source.updatedBy)}</span>
              </div>
            </div>

            {error && <div className={styles.errorNotice}>{error}</div>}

            {editing ? (
              <>
                <div className={styles.field}>
                  <label className={styles.label} htmlFor="sdm-description">
                    Description <span className={styles.optional}>(optional)</span>
                  </label>
                  <textarea
                    id="sdm-description"
                    className={styles.textarea}
                    rows={3}
                    placeholder="Briefly describe what this source covers"
                    value={description}
                    onChange={e => setDescription(e.target.value)}
                  />
                </div>

                {isWeb && (
                  <div className={styles.field}>
                    <label className={styles.label}>URL</label>
                    <a className={styles.link} href={ensureHref(source.url)} target="_blank" rel="noreferrer">
                      {source.url}
                    </a>
                  </div>
                )}

                <div className={styles.field}>
                  <label className={styles.label} htmlFor="sdm-products">Products</label>
                  <AssociatedProductInput
                    id="sdm-products"
                    selected={selectedProducts}
                    availableProducts={products}
                    onChange={setSelectedProducts}
                    placeholder="Link a product…"
                  />
                </div>

                <div className={styles.field}>
                  <label className={styles.label} htmlFor="sdm-review-date">Review Date</label>
                  <input
                    id="sdm-review-date"
                    className={styles.input}
                    type="date"
                    value={reviewDate}
                    onChange={e => setReviewDate(e.target.value)}
                  />
                </div>
              </>
            ) : (
              <>
                {source.description && (
                  <div className={styles.section}>
                    <span className={styles.metaLabel}>Description</span>
                    <span className={styles.descriptionText}>{source.description}</span>
                  </div>
                )}

                <div className={styles.section}>
                  <span className={styles.metaLabel}>Products</span>
                  {sourceProducts.length === 0 ? (
                    <span className={styles.metaValue}>No products linked</span>
                  ) : (
                    <div className={styles.productChips}>
                      {sourceProducts.map(product => <ProductChip key={product} name={product} />)}
                    </div>
                  )}
                </div>

                <div className={styles.section}>
                  <div className={styles.sectionHeader}>
                    <span className={styles.metaLabel}>Content Sets</span>
                    {/* Documents only — a web source's content is filled by the
                        scraper against the URL, not by uploading a file here. */}
                    {!isWeb && onAddVersion && (
                      <button
                        type="button"
                        className={styles.addVersionBtn}
                        onClick={() => onAddVersion(source)}
                      >
                        + Add Version
                      </button>
                    )}
                  </div>
                  <ContentSetList
                    contentSets={contentSets}
                    onDelete={onDeleteVersion ? cs => onDeleteVersion(source, cs) : undefined}
                    emptyLabel={isWeb ? 'Not yet crawled.' : 'No file uploaded yet.'}
                  />
                </div>

                {isWeb && (
                  <div className={styles.section}>
                    <span className={styles.metaLabel}>URL</span>
                    <a className={styles.link} href={ensureHref(source.url)} target="_blank" rel="noreferrer">
                      {source.url}
                    </a>
                  </div>
                )}
              </>
            )}

          </div>

          {/* Footer */}
          <div className={styles.footer}>
            {editing ? (
              <>
                <button
                  type="button"
                  className={`${styles.footerBtn} ${styles.cancelBtn}`}
                  onClick={cancelEdit}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className={`${styles.footerBtn} ${styles.submitBtn}`}
                  disabled={saving}
                >
                  {saving ? 'Saving…' : 'Save Changes'}
                </button>
              </>
            ) : (
              <button
                type="button"
                className={`${styles.footerBtn} ${styles.cancelBtn}`}
                onClick={onClose}
              >
                Close
              </button>
            )}
          </div>
        </form>

      </div>
    </div>
  )
}
